import { useCallback, useState } from 'react';

import {
  getMaterialUnitOfMeasurement,
  getPluralizedUnitOfMeasurement,
} from 'approot/activity-builder/components/teaching-notes/lesson-materials/units-of-measurement/units-of-measurement-utils';
import { LessonMaterialFragment } from './__generated__/LessonMaterialFragment';
import LessonMaterialsAlternatives from './lesson-materials-alternatives';
import {
  LessonMaterialSmallText,
  LessonMaterialTextItalic,
} from './lesson-materials-list.style';
import {
  LinkButton,
  PrimaryLinkButton,
} from 'approot/shared/styles/buttons/link-button.style';
import { ScreenReaderOnly } from 'approot/shared/styles/global.styles';
import { PStyled } from 'approot/shared/styles/typography.style';
import TooltipInfo from 'approot/shared/tooltip/tooltip-info';
import { LessonMaterialsCalculatorTableStyled } from './lesson-materials-calculator.style';

type CE2 = {
  CE2: {
    showSurvey: (id: string) => void;
  };
};

declare var window: CE2;

const PREVIEW_LIMIT = 5;

const LessonMaterialsCalculatorTable = ({
  materials,
}: {
  materials: LessonMaterialFragment[];
}) => {
  const handleShowSurvey = useCallback(() => {
    if (window.CE2) {
      window.CE2.showSurvey('c68d847d-f2cd-4fc1-88cf-a1eff5e0d907');
    }
  }, []);
  const [isPreview, setIsPreview] = useState<boolean>(true);
  const hasMoreMaterials = materials.length > PREVIEW_LIMIT;

  return (
    <>
      <LessonMaterialsCalculatorTableStyled aria-label="Lesson Materials Calculator">
        <thead>
          <tr>
            <ScreenReaderOnly as="th">Material</ScreenReaderOnly>
            <ScreenReaderOnly as="th">Quantity</ScreenReaderOnly>
          </tr>
        </thead>
        <tbody>
          {(isPreview ? materials.slice(0, PREVIEW_LIMIT) : materials).map(
            (material, index) => {
              const materialUnitOfMeasurement = material?.unitsOfMeasurement
                ?.length
                ? getMaterialUnitOfMeasurement(material)?.name || ''
                : '';
              return (
                <tr key={`${index}-${material.id}`}>
                  <td>
                    {material.name}
                    {material.note && (
                      <TooltipInfo hoverText={material.note} multiline />
                    )}
                    {material.isOptional && (
                      <LessonMaterialTextItalic>
                        {` Optional`}
                      </LessonMaterialTextItalic>
                    )}
                    {!!material.alternativeMaterials?.length && (
                      <LessonMaterialsAlternatives
                        alternativeMaterials={material.alternativeMaterials}
                      />
                    )}
                  </td>
                  <td>
                    <PStyled marginBottom={0}>
                      {`${material.numberOfItems} `}
                      {getPluralizedUnitOfMeasurement(
                        materialUnitOfMeasurement,
                        material.numberOfItems
                      )}
                    </PStyled>
                  </td>
                </tr>
              );
            }
          )}
        </tbody>
      </LessonMaterialsCalculatorTableStyled>
      {hasMoreMaterials && (
        <LessonMaterialSmallText>
          <LinkButton
            type="button"
            aria-label={`Show ${isPreview ? 'more' : 'less'} materials`}
            onClick={() => setIsPreview(!isPreview)}
          >
            {`Read ${isPreview ? 'More' : 'Less'}`}
          </LinkButton>
        </LessonMaterialSmallText>
      )}
      <LessonMaterialSmallText>
        Help us improve! Share your feedback on our lesson materials calculator{' '}
        <PrimaryLinkButton
          type="button"
          onClick={handleShowSurvey}
          aria-label="Share your feedback on our lesson materials calculator"
        >
          here
        </PrimaryLinkButton>
        .
      </LessonMaterialSmallText>
    </>
  );
};

export default LessonMaterialsCalculatorTable;
